// Activity feed client — drives the social-proof toast, ticker and home widget.
// 30-second polling matches the existing inbox cadence so we don't add a new
// timer category. The hook keeps a small in-memory queue of "new since last
// poll" events so the toast popper can iterate over them without re-pulling.
import axios from "axios";
import { useEffect, useRef, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const activityApi = {
  feed: (limit = 30) =>
    axios.get(`${API}/activity/feed`, { params: { limit } }).then((r) => r.data),
  stats: () => axios.get(`${API}/activity/stats`).then((r) => r.data),
  log: (payload) => axios.post(`${API}/activity/log`, payload).then((r) => r.data),
};

/**
 * useActivityFeed — returns { feed, stats, newEvents, ack }
 *  - feed: rolling list of latest events (max ~30).
 *  - stats: aggregate counters (active 24h, mdm month, etc).
 *  - newEvents: events that arrived since the last poll AND haven't been
 *    acknowledged yet via ack(id). The toast popper consumes one at a time
 *    and calls ack(id) when done so it doesn't replay.
 *
 * Activation rules:
 *  - Skipped entirely when no auth token (logged-out routes).
 *  - 30s interval (configurable via `pollMs`).
 *  - Initial fetch happens immediately so the home widget isn't empty.
 */
export function useActivityFeed({ enabled = true, pollMs = 30_000 } = {}) {
  const [feed, setFeed] = useState([]);
  const [stats, setStats] = useState(null);
  const [newEvents, setNewEvents] = useState([]);
  const seenIds = useRef(new Set());
  const ackedIds = useRef(new Set());
  const firstPoll = useRef(true);

  const ack = (id) => {
    ackedIds.current.add(id);
    setNewEvents((prev) => prev.filter((e) => e.id !== id));
  };

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const tick = async () => {
      // iter150-review — skip entirely while tab is hidden or offline.
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      try {
        const [rows, s] = await Promise.all([activityApi.feed(30), activityApi.stats()]);
        if (cancelled) return;
        setFeed(rows);
        setStats(s);
        // First poll seeds the seenIds set so we don't toast every existing
        // event right after login. Only events ARRIVING after first poll
        // count as "new".
        if (firstPoll.current) {
          rows.forEach((r) => seenIds.current.add(r.id));
          firstPoll.current = false;
          return;
        }
        const fresh = rows.filter((r) => !seenIds.current.has(r.id) && !ackedIds.current.has(r.id));
        fresh.forEach((r) => seenIds.current.add(r.id));
        if (fresh.length) {
          setNewEvents((prev) => {
            // Cap at 5 pending toasts so a flood doesn't queue forever.
            const merged = [...prev, ...fresh];
            return merged.slice(-5);
          });
        }
      } catch (err) {
        // Swallow — feed is decorative.
      }
    };
    tick();
    const t = setInterval(tick, pollMs);
    const onVisibility = () => { if (document.visibilityState === "visible") tick(); };
    const onOnline = () => tick();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);
    return () => {
      cancelled = true;
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
    };
  }, [enabled, pollMs]);

  return { feed, stats, newEvents, ack };
}

// Human-friendly relative timestamp ("just now", "5 min ago", "2 h ago").
export function relativeTime(iso) {
  if (!iso) return "";
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return "";
  const sec = Math.max(0, Math.floor((Date.now() - dt.getTime()) / 1000));
  if (sec < 30) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} h ago`;
  const day = Math.floor(hr / 24);
  if (day === 1) return "yesterday";
  if (day < 7) return `${day} d ago`;
  return dt.toLocaleDateString();
}

// Map event type → visual hint (icon + colour) used by all three surfaces.
export const ACTIVITY_VISUAL = {
  school_joined: { icon: "🏫", color: "moss", label: "New School" },
  mdm_bill: { icon: "📊", color: "clay", label: "MDM Bill" },
  uc_generated: { icon: "📄", color: "ochre", label: "Quarterly UC" },
  voucher_generated: { icon: "🧾", color: "moss-dark", label: "Voucher" },
  setup_completed: { icon: "✅", color: "moss", label: "Setup Done" },
};
